export interface CampaignTypeDefinition {
  name: string;
  possibleGoals: string[];
  defaultKPIs: {
    activity: string[];   // Activity-specific KPIs (e.g., webinar attendance, report downloads)
    business: string[];   // Business outcome KPIs (e.g., service sales)
  };
  suggestedChannels: string[];
  timelineTemplate?: {
    before: string[];
    during: string[];
    after: string[];
  };
}

export const CAMPAIGN_TYPES: Record<string, CampaignTypeDefinition> = {
  'webinar': {
    name: 'Webinar',
    possibleGoals: [
      'Lead generation',
      'Product/service sales',
      'Brand awareness',
      'Education/thought leadership',
      'Market research',
      'Community building'
    ],
    defaultKPIs: {
      activity: [
        'Number of registrations',
        'Attendance rate',
        'Average watch time',
        'Live engagement rate (questions, comments)',
        'Replay views',
        'Content download rate'
      ],
      business: [
        'Lead quality score',
        'Sales qualified leads (SQL)',
        'Conversion rate to sales',
        'Revenue generated',
        'Return on investment (ROI)',
        'Customer acquisition cost (CAC)'
      ]
    },
    suggestedChannels: [
      'Email marketing',
      'LinkedIn',
      'Website',
      'Partner networks',
      'Industry forums'
    ],
    timelineTemplate: {
      before: [
        'Landing page setup',
        'Email campaign preparation',
        'Social media promotion',
        'Partner/speaker coordination',
        'Content preparation',
        'Technical setup and testing'
      ],
      during: [
        'Live moderation',
        'Engagement monitoring',
        'Technical support',
        'Social media live updates'
      ],
      after: [
        'Follow-up emails',
        'Content distribution',
        'Lead scoring and qualification',
        'Sales team handoff',
        'Feedback collection',
        'Performance analysis'
      ]
    }
  }
  // Add other campaign types here
};

export interface BriefFieldDefinition {
  key: keyof BriefData;
  label: string;
  explanation: string;
  type: 'text' | 'picklist' | 'multiline' | 'date';
  options?: string[];  // For picklist fields
  required: boolean;
  aiExtract?: {  // New field for AI extraction rules
    type: 'goal' | 'event_type' | 'date' | 'none';
    relatedFields?: string[];  // Fields that should be updated based on this extraction
  };
}

export interface BriefData {
  campaignName: string;
  campaignType: string;
  primaryObjective: string;    // The business goal (e.g., service sales)
  eventObjective: string;      // The activity-specific goal (e.g., audience engagement)
  eventDate: string;
  targetAudience: string;
  budget: string;
  timeline: string;
  keyMessages: string;
  channels: string;
  activityKPIs: string;       // Activity-specific KPIs
  businessKPIs: string;       // Business outcome KPIs
  creativeAssets: string;
  additionalNotes: string;
}

export const BRIEF_FIELDS: BriefFieldDefinition[] = [
  {
    key: 'campaignName',
    label: 'Campaign Name',
    explanation: 'Name of your campaign',
    type: 'text',
    required: true,
    aiExtract: { type: 'none' }
  },
  {
    key: 'campaignType',
    label: 'Campaign Type',
    explanation: 'Type of marketing campaign',
    type: 'picklist',
    required: true,
    options: Object.keys(CAMPAIGN_TYPES),
    aiExtract: { type: 'event_type' }
  },
  {
    key: 'primaryObjective',
    label: 'Primary Business Objective',
    explanation: 'What business outcome do you want to achieve?',
    type: 'picklist',
    required: true,
    options: CAMPAIGN_TYPES['webinar'].possibleGoals,
    aiExtract: { type: 'goal' }
  },
  {
    key: 'eventObjective',
    label: 'Event Objective',
    explanation: 'What do you want to achieve during the event itself?',
    type: 'text',
    required: true,
    aiExtract: { type: 'goal' }
  },
  {
    key: 'eventDate',
    label: 'Data Wydarzenia',
    explanation: 'Data głównego wydarzenia kampanii',
    type: 'date',
    required: false,
    aiExtract: { type: 'date' }
  },
  {
    key: 'targetAudience',
    label: 'Grupa Docelowa',
    explanation: 'Szczegółowy opis idealnego klienta, w tym dane demograficzne i behawioralne',
    type: 'multiline',
    required: true,
    aiExtract: { type: 'none' }
  },
  {
    key: 'budget',
    label: 'Budżet',
    explanation: 'Całkowity budżet kampanii',
    type: 'text',
    required: true,
    aiExtract: { type: 'none' }
  },
  {
    key: 'timeline',
    label: 'Harmonogram',
    explanation: 'Czas trwania kampanii i kluczowe daty',
    type: 'text',
    required: true,
    aiExtract: { type: 'none' }
  },
  {
    key: 'keyMessages',
    label: 'Kluczowe Przekazy',
    explanation: 'Główne komunikaty i wartości do przekazania w kampanii',
    type: 'multiline',
    required: true,
    aiExtract: { type: 'none' }
  },
  {
    key: 'channels',
    label: 'Kanały',
    explanation: 'Kanały marketingowe do wykorzystania w kampanii',
    type: 'picklist',
    options: [
      'Social Media',
      'Email Marketing',
      'Content Marketing',
      'Reklama Płatna',
      'PR',
      'Wydarzenia',
      'SEO',
      'Marketing Influencerski',
      'Direct Mail',
      'TV/Radio',
      'Media Drukowane',
      'Inne'
    ],
    required: true,
    aiExtract: { type: 'none' }
  },
  {
    key: 'activityKPIs',
    label: 'Activity KPIs',
    explanation: 'Key performance indicators for the campaign activity itself',
    type: 'picklist',
    options: CAMPAIGN_TYPES['webinar'].defaultKPIs.activity,
    required: true,
    aiExtract: { type: 'none' }
  },
  {
    key: 'businessKPIs',
    label: 'Business KPIs',
    explanation: 'Business outcome KPIs',
    type: 'picklist',
    options: CAMPAIGN_TYPES['webinar'].defaultKPIs.business,
    required: true,
    aiExtract: { type: 'none' }
  },
  {
    key: 'creativeAssets',
    label: 'Materiały',
    explanation: 'Wymagane materiały kreatywne i treści do kampanii',
    type: 'multiline',
    required: false,
    aiExtract: { type: 'none' }
  },
  {
    key: 'additionalNotes',
    label: 'Dodatkowe Uwagi',
    explanation: 'Inne ważne informacje lub specjalne wymagania',
    type: 'multiline',
    required: false,
    aiExtract: { type: 'none' }
  }
]; 